import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { BASE as API_BASE, setPostAuthRedirect } from '../services/api';
import { toast } from '../utils/toastBus';
import { Button, Card } from '../components/ui';

export default function AdComposer() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState(8);
  const [busy, setBusy] = useState(false);
  const [videoBusy, setVideoBusy] = useState(false);
  const [result, setResult] = useState(null); // { item, assets, spec }
  const [aspect, setAspect] = useState('16:9');
  const [videoUrl, setVideoUrl] = useState('');
  const [adVideoId, setAdVideoId] = useState('');
  const videoRef = useRef(null);
  const assetsReady = !!(result?.assets?.audioPath && result?.assets?.imageUrl);

  const ensureAudioReady = React.useCallback(() => {
    const audioPath = result?.assets?.audioPath;
    if (!audioPath) {
      toast({ type: 'warning', message: 'Generate or upload audio before finalizing video.' });
      return false;
    }
    return true;
  }, [result?.assets?.audioPath]);
  const [imgOverride, setImgOverride] = useState('');
  const [imgBusy, setImgBusy] = useState(false);
  const [videoMode, setVideoMode] = useState('motion'); // static | motion | animated
  const [tier, setTier] = useState('veo3'); // veo3 | kling | runway | opensora
  const [predId, setPredId] = useState('');
  const [predStatus, setPredStatus] = useState('');
  const [providerMeta, setProviderMeta] = useState(null);
  const [credits, setCredits] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [estimCredits, setEstimCredits] = useState(null);
  const [sfxCatalog, setSfxCatalog] = useState([]);
  const [sfxSelected, setSfxSelected] = useState([]); // [{ name, atMs }]
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [tailPadSec, setTailPadSec] = useState(0.25);
  const [voFadeOutSec, setVoFadeOutSec] = useState(0);
  const [voiceOffsetMs, setVoiceOffsetMs] = useState(500);
  const [visualPreset, setVisualPreset] = useState('auto'); // auto|flames|lasers|embers|grid
  const [musicBed, setMusicBed] = useState(false);
  const [musicTrack, setMusicTrack] = useState(''); // '', 'bed_trap.mp3', 'bed_edm.mp3'
  const [simBusy, setSimBusy] = useState(false);
  const [variants, setVariants] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    // No auto-play; user gesture will start playback with sound via native controls
  }, [videoUrl]);

  // Restore draft state on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem('adcomposer_draft_v1');
      if (!raw) return;
      const j = JSON.parse(raw);
      if (j && typeof j === 'object') {
        if (typeof j.prompt === 'string') setPrompt(j.prompt);
        if (typeof j.title === 'string') setTitle(j.title);
        if (typeof j.duration === 'number') setDuration(j.duration);
        if (typeof j.aspect === 'string') setAspect(j.aspect);
        if (typeof j.videoMode === 'string') setVideoMode(j.videoMode);
        if (typeof j.tier === 'string') setTier(j.tier);
        if (Array.isArray(j.sfxSelected)) setSfxSelected(j.sfxSelected);
        if (j.result && j.result.assets) setResult(r => ({ ...(r||{}), assets: j.result.assets }));
        if (j.predictionId) {
          // Resume polling for pending video
          setPredId(j.predictionId);
          setPredStatus('starting');
          if (j.providerMeta) setProviderMeta(j.providerMeta);
          resumePolling(j.predictionId);
        }
      }
    } catch {}
  }, []);

  // Fetch prompt suggestions (admin-only composer API)
  useEffect(() => {
    (async () => {
      try {
        const r = await api.composerPromptSuggestions(8);
        if (Array.isArray(r?.items)) setSuggestions(r.items);
      } catch {}
    })();
  }, []);

  // Persist draft on change
  useEffect(() => {
    try {
      const draft = {
        prompt,
        title,
        duration,
        aspect,
        videoMode,
        tier,
        sfxSelected,
        adv: { showAdvanced, tailPadSec, voFadeOutSec, voiceOffsetMs, visualPreset, musicBed, musicTrack },
        result: result && result.assets ? { assets: result.assets } : null,
        predictionId: predId,
        providerMeta,
      };
      localStorage.setItem('adcomposer_draft_v1', JSON.stringify(draft));
    } catch {}
  }, [prompt, title, duration, aspect, videoMode, tier, sfxSelected, showAdvanced, tailPadSec, voFadeOutSec, voiceOffsetMs, visualPreset, musicBed, musicTrack, result?.assets?.audioPath, result?.assets?.imageUrl, predId, providerMeta]);

  // Load credits balance (if signed in)
  useEffect(() => {
    (async () => {
      try {
        const r = await api.getMyCredits();
        if (r && typeof r.balance === 'number') setCredits(Number(r.balance));
        else setCredits(null);
      } catch {
        setCredits(null);
      } finally {
        setCreditsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/assets/sfx`, { credentials: 'include' });
        const j = await r.json().catch(() => ({ items: [] }));
        if (Array.isArray(j?.items)) setSfxCatalog(j.items.filter(it => it?.exists));
      } catch {}
    })();
  }, []);

  const finalizeVideo = React.useCallback(async (predictionId) => {
    if (!assetsReady) {
      toast({ type: 'error', message: 'Finalize blocked: generate audio + image first.' });
      throw new Error('assets_not_ready');
    }
    const payload = {
      predictionId,
      streamId: result?.item?._id || result?.item?.id || undefined,
      audioPath: result?.assets?.audioPath,
      imageUrl: result?.assets?.imageUrl,
      durationSec: duration,
      aspectRatio: aspect,
      sfx: sfxSelected,
      ...(showAdvanced ? {
        tailPadSec: Number(tailPadSec) || 0,
        voFadeOutSec: Number(voFadeOutSec) || 0,
        voiceOffsetMs: Number(voiceOffsetMs) || 0,
        visualPreset: visualPreset,
        musicBed: musicBed,
        musicTrack: musicTrack || undefined,
      } : {}),
    };
    let lastErr = '';
    for (let attempt = 0; attempt < 3; attempt++) {
      const fin = await api.adVideoFinalize(payload);
      if (!fin?.error) {
        const url = fin?.item?.videoUrl;
        const avid = fin?.meta?.adVideoId || fin?.item?.adVideoId;
        if (fin?.meta) setProviderMeta(m => ({ ...(m || {}), ...fin.meta }));
        if (url) {
          setVideoUrl(url);
          if (avid) setAdVideoId(String(avid));
          toast({ type: 'success', message: attempt ? 'Video ready after retry' : 'Video ready' });
          return;
        }
        lastErr = 'finalize_missing_url';
        break;
      }
      lastErr = fin.error;
      if (fin.error === 'not_ready') {
        await new Promise(r => setTimeout(r, 2500));
        continue;
      }
      break;
    }
    throw new Error(lastErr || 'video_finalize_failed');
  }, [assetsReady, result?.item?._id, result?.item?.id, result?.assets?.audioPath, result?.assets?.imageUrl, duration, aspect, sfxSelected, showAdvanced, tailPadSec, voFadeOutSec, voiceOffsetMs, visualPreset, musicBed, musicTrack]);

  // Resume polling for pending video
  async function resumePolling(predId) {
    try {
      let status = 'starting';
      let tries = 0;
      while (tries < 120) {
        const st = await api.adVideoStatus(predId);
        if (st?.error) throw new Error(st.error || st.message || 'video_status_failed');
        status = st?.status || 'unknown';
        setPredStatus(status);
        if (st?.meta) setProviderMeta(st.meta);
        if (status === 'succeeded' || status === 'failed' || status === 'canceled') break;
        await new Promise(r => setTimeout(r, 2000));
        tries++;
      }
      if (status === 'failed' || status === 'canceled') {
        toast({ type: 'error', message: `Video generation ${status}` });
        return;
      }
      if (status === 'succeeded') {
        await finalizeVideo(predId);
      } else {
        throw new Error(`video_status_${status || 'unknown'}`);
      }
    } catch (e) {
      const msg = e?.message || 'Resume failed';
      toast({ type: 'error', message: `Video generation failed: ${msg}` });
      try { console.warn('[adVideo] resumePolling error', { predId, msg }); } catch {}
    }
  }

  const requiresPurchase = !creditsLoading && (credits === null || (Number.isFinite(Number(estimCredits)) && Number.isFinite(Number(credits)) && Number(credits) < Number(estimCredits)));

  const handleBuyTokens = React.useCallback(async (packId = '100') => {
    try {
      const res = await api.startCreditsPurchase(packId);
      if (res?.requiresAuth) {
        setPostAuthRedirect('/profile');
        navigate('/login?redirectTo=/profile');
        return;
      }
      if (res?.error) {
        toast({ type: 'error', message: res.error });
      }
    } catch (err) {
      toast({ type: 'error', message: err?.message || 'Checkout unavailable' });
    }
  }, [navigate]);

  const ensureCreditsAvailable = () => {
    if (creditsLoading) return false;
    if (credits === null) {
      toast({ type: 'warning', message: 'Sign in and purchase tokens to use Ad Composer.' });
      handleBuyTokens();
      return false;
    }
    if (Number.isFinite(Number(estimCredits)) && Number.isFinite(Number(credits)) && Number(credits) < Number(estimCredits)) {
      toast({ type: 'warning', message: 'Add more tokens before composing this ad.' });
      handleBuyTokens();
      return false;
    }
    return true;
  };

  function addSfxQuick(it) {
    const nm = String(it?.name || '').toLowerCase();
    if (!nm) return;
    const endMs = Math.max(0, Number(duration) * 1000);
    let defaults = [];
    const cat = (it?.category || '').toLowerCase();
    if (cat === 'hit') {
      defaults = [{ name: nm, atMs: 0 }, { name: nm, atMs: 2000 }];
    } else if (cat === 'swell' || nm.includes('swell')) {
      defaults = [{ name: nm, atMs: Math.max(0, endMs - 1500) }];
    } else if (cat === 'whoosh') {
      defaults = [{ name: nm, atMs: 400 }];
    } else if (cat === 'ui') {
      defaults = [{ name: nm, atMs: 100 }];
    } else if (cat === 'riser') {
      defaults = [{ name: nm, atMs: Math.max(0, endMs - 1200) }];
    } else {
      defaults = [{ name: nm, atMs: 0 }];
    }
    setSfxSelected(prev => {
      const merged = [...prev];
      for (const d of defaults) merged.push(d);
      return merged.slice(0, 12);
    });
  }

  function removeSfxAt(index) {
    setSfxSelected(prev => prev.filter((_, i) => i !== index));
  }

  // Approximate estimator aligned with backend defaults per tier
  useEffect(() => {
    try {
      const secs = Math.max(1, Math.round(Number(duration || 8)));
      const isVeo3 = String(tier) === 'veo3';
      const isHigh = String(tier) === 'high';
      const isPremium1080 = isVeo3 || isHigh;
      const resolution = isPremium1080 ? '1080p' : (aspect === '16:9' ? '720p' : '480p');
      const uplift1080p = String(resolution).toLowerCase() === '1080p' ? 0.2 : 0;
      const upliftPremium = isVeo3 ? 0.15 : 0; // premium uplift only for Veo-3
      const ops = 1;
      const allowSet = isPremium1080 ? [4,6,8] : [];
      const snapped = allowSet.length ? allowSet.reduce((b,d)=>Math.abs(secs-d)<Math.abs(b-secs)?d:b, allowSet[0]) : secs;
      // Tier multipliers
      let factor = 1.0;
      if (String(tier) === 'low') factor = 0.4; else if (String(tier) === 'medium') factor = 0.6; else if (String(tier) === 'high') factor = 0.8; else factor = 1.0;
      const total = Math.ceil(snapped * (1+uplift1080p+upliftPremium) * factor + ops);
      setEstimCredits(total);
    } catch { setEstimCredits(null); }
  }, [duration, aspect, videoMode, tier]);

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 style={{ color: '#ff7a45', margin: 0 }}>Ad Composer</h2>
        <div style={{ marginLeft: 'auto' }}>
          <Link 
            to="/discover"
            style={{
              padding: '6px 10px',
              background: '#374151',
              color: '#e5e7eb',
              borderRadius: 8,
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 500
            }}
          >Discover Ads →</Link>
        </div>
      </div>

      <Card title="Compose from Prompt" subtitle="Enter your marketing prompt. We'll generate audio + an image.">
        {requiresPurchase && (
          <div style={{ border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.1)', color: '#fbbf24', borderRadius: 8, padding: 12, marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Tokens required</div>
            <div style={{ fontSize: 14, color: '#fde68a' }}>
              Purchase credits to compose new ads. <button onClick={() => handleBuyTokens()} style={{ background: 'transparent', border: 0, color: '#fbbf24', textDecoration: 'underline', cursor: 'pointer' }}>Buy tokens</button>
            </div>
          </div>
        )}
        <div style={{ display: 'grid', gap: 12 }}>
          <textarea
            placeholder="Paste your marketing prompt here..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={6}
            style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb', fontSize: 16 }}
          />
          {!!suggestions.length && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  const list = suggestions;
                  if (list.length) {
                    const pick = list[Math.floor(Math.random() * list.length)];
                    setPrompt((p) => (p?.trim()?.length ? `${p}\n${pick}` : pick));
                    try { toast({ type: 'success', message: 'Suggestion applied' }); } catch {}
                  }
                }}
                style={{ background: '#1f2937', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}
              >Surprise me</button>
              <select
                onChange={(e) => { const v = e.target.value; if (v) setPrompt(v); }}
                defaultValue=""
                style={{ background: '#0b0b0e', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 8, padding: '6px 8px' }}
              >
                <option value="">Pick a suggestion…</option>
                {suggestions.map((s, idx) => (
                  <option key={`${idx}`} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 12 }}>
            <input
              placeholder="Optional title (defaults to 'Ad Stream')"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb', fontSize: 16 }}
            />
            <input
              type="number"
              min={5}
              max={120}
              step={5}
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value) || 30)}
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb', fontSize: 16 }}
            />
          </div>
          {/* Advanced Options */}
          <div style={{ borderTop: '1px solid #1f2937', paddingTop: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#e5e7eb' }}>
              <input type="checkbox" checked={showAdvanced} onChange={(e)=>setShowAdvanced(e.target.checked)} />
              Extra Options
            </label>
            {showAdvanced && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 8 }}>
                <div>
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>VO Tail Pad (sec)</div>
                  <input type="number" step={0.05} min={0} value={tailPadSec} onChange={(e)=>setTailPadSec(Number(e.target.value) || 0)} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb' }} />
                </div>
                <div>
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>VO Fade Out Max (sec)</div>
                  <input type="number" step={0.05} min={0} value={voFadeOutSec} onChange={(e)=>setVoFadeOutSec(Number(e.target.value) || 0)} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb' }} />
                </div>
                <div>
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>VO Offset (ms)</div>
                  <input type="number" step={50} min={0} value={voiceOffsetMs} onChange={(e)=>setVoiceOffsetMs(Number(e.target.value) || 0)} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb' }} />
                </div>
              </div>
            )}
            {showAdvanced && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 8 }}>
                <div>
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>Visual Preset</div>
                  <select value={visualPreset} onChange={(e)=>setVisualPreset(e.target.value)} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb' }}>
                    <option value="auto">Auto (from prompt)</option>
                    <option value="flames">Flames</option>
                    <option value="lasers">Lasers</option>
                    <option value="embers">Embers</option>
                    <option value="grid">Grid</option>
                  </select>
                </div>
                <div>
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>Music Bed</div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={musicBed} onChange={(e)=>setMusicBed(e.target.checked)} />
                    Enable
                  </label>
                </div>
                <div>
                  <div style={{ color: '#9ca3af', fontSize: 12 }}>Music Track</div>
                  <select disabled={!musicBed} value={musicTrack} onChange={(e)=>setMusicTrack(e.target.value)} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb' }}>
                    <option value="">Default</option>
                    <option value="bed_trap.mp3">Trap Bed</option>
                    <option value="bed_edm.mp3">EDM Bed</option>
                  </select>
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Button
              loading={busy}
              disabled={busy || !prompt.trim() || requiresPurchase}
              onClick={async () => {
                try {
                  if (!ensureCreditsAvailable()) return;
                  setBusy(true);
                  setResult(null);
                  const res = await api.adComposeFull({ prompt, durationSec: duration, title });
                  if (res?.error) throw new Error(res.error);
                  setResult(res);
                  toast({ type: 'success', message: 'Ad composed successfully' });
                } catch (e) {
                  toast({ type: 'error', message: e?.message || 'Compose failed' });
                } finally {
                  setBusy(false);
                }
              }}
            >{busy ? 'Composing…' : 'Compose'}</Button>
            <Button
              loading={simBusy}
              disabled={simBusy || !prompt.trim() || requiresPurchase}
              onClick={async () => {
                try {
                  if (!ensureCreditsAvailable()) return;
                  setSimBusy(true);
                  const payload = { prompt, durationSec: duration, tier, aspectRatio: aspect, musicBed, musicTrack, sfx: sfxSelected };
                  const r = await api.adSimulate(payload);
                  if (r?.ok && Array.isArray(r.variants)) {
                    setVariants(r.variants);
                    toast({ type: 'success', message: 'Simulated variants' });
                  } else {
                    setVariants([]);
                    toast({ type: 'warning', message: 'No variants available' });
                  }
                } catch (e) {
                  toast({ type: 'error', message: e?.message || 'Simulation failed' });
                } finally {
                  setSimBusy(false);
                }
              }}
            >{simBusy ? 'Simulating…' : 'Simulate Variants'}</Button>
            {result?.item && (
              <Link to={`/streams/${encodeURIComponent(String(result.item._id || result.item.id))}`} target="_blank" rel="noopener noreferrer" style={{ color: '#93c5fd' }}>Open Stream</Link>
            )}
          </div>
          {!!variants.length && (
            <div style={{ marginTop: 8, borderTop: '1px solid #1f2937', paddingTop: 8 }}>
              <div style={{ color: '#e5e7eb', marginBottom: 6 }}>Variant Compare (heuristic “Estimated score”)</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 12 }}>
                {variants.map((v, idx) => (
                  <div key={`${v.id}_${idx}`} style={{ border: '1px solid #374151', borderRadius: 8, padding: 10, background: '#0b0b0e' }}>
                    <div style={{ color: '#ff7a45', fontWeight: 600, marginBottom: 4 }}>Variant {v.id || idx+1}</div>
                    <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 6 }}>Estimated score: {typeof v.estimatedScore === 'number' ? v.estimatedScore : 'n/a'}</div>
                    <div style={{ color: '#e5e7eb', fontSize: 12, display: 'grid', gap: 2 }}>
                      <div>Tier: {String(v?.params?.tier || '')}</div>
                      <div>Aspect: {String(v?.params?.aspectRatio || '')}</div>
                      <div>Music: {String(v?.params?.musicBed ? (v?.params?.musicTrack || 'on') : 'off')}</div>
                      <div>SFX: {Array.isArray(v?.params?.sfx) ? v.params.sfx.length : 0}</div>
                    </div>
                    <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                      <Button
                        onClick={() => {
                          try {
                            const p = v.params || {};
                            if (p.tier) setTier(String(p.tier));
                            if (p.aspectRatio) setAspect(String(p.aspectRatio));
                            if (typeof p.musicBed === 'boolean') setMusicBed(!!p.musicBed);
                            if (typeof p.musicTrack === 'string') setMusicTrack(p.musicTrack);
                            if (Array.isArray(p.sfx)) setSfxSelected(p.sfx.map(x => ({ name: String(x.name || x), atMs: Math.max(0, Number(x.atMs || 0)) })));
                            toast({ type: 'success', message: 'Variant applied' });
                          } catch {}
                        }}
                      >Apply Variant</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result?.item && (
            <div style={{ display: 'grid', gap: 12 }}>
              {result.assets?.imageUrl && (
                <img
                  src={result.assets.imageUrl.startsWith('/uploads/') ? `${API_BASE}${result.assets.imageUrl}` : result.assets.imageUrl}
                  alt="cover"
                  loading="lazy"
                  decoding="async"
                  fetchpriority="low"
                  style={{ maxWidth: 320, borderRadius: 8, border: '1px solid #374151' }}
                />
              )}
              {/* Image override + regenerate */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                <input
                  placeholder="Image prompt (override, optional)"
                  value={imgOverride}
                  onChange={(e) => setImgOverride(e.target.value)}
                  style={{ padding: 10, borderRadius: 8, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb', fontSize: 14 }}
                />
                <Button
                  loading={imgBusy}
                  disabled={imgBusy || !result?.item}
                  onClick={async () => {
                    try {
                      setImgBusy(true);
                      const override = imgOverride.trim();
                      if (!override) throw new Error('Enter an image prompt to regenerate');
                      const r = await api.generateImage({ prompt: override, aspect_ratio: aspect || '1:1' });
                      if (r?.item?.url) {
                        setResult(prev => prev ? { ...prev, assets: { ...prev.assets, imageUrl: r.item.url } } : prev);
                        setVideoUrl('');
                        toast({ type: 'success', message: 'Image regenerated' });
                      } else {
                        throw new Error(r?.error || 'Image generation failed');
                      }
                    } catch (e) {
                      toast({ type: 'error', message: e?.message || 'Regenerate failed' });
                    } finally {
                      setImgBusy(false);
                    }
                  }}
                >{imgBusy ? 'Regenerating…' : 'Regenerate Image'}</Button>
              </div>
              {result.assets?.audioPath && (
                <audio
                  src={result.assets.audioPath.startsWith('/uploads/') ? `${API_BASE}${result.assets.audioPath}` : result.assets.audioPath}
                  controls
                  preload="metadata"
                  style={{ width: '100%' }}
                />
              )}
              {videoUrl && (
                <div style={{ display: 'grid', gap: 8 }}>
                  <video
                    ref={videoRef}
                    src={videoUrl.startsWith('/uploads/') ? `${API_BASE}${videoUrl}` : videoUrl}
                    controls
                    muted={false}
                    autoPlay={false}
                    playsInline
                    preload="metadata"
                    style={{ width: '100%', maxWidth: 480, borderRadius: 8, border: '1px solid #374151' }}
                    poster={result.assets?.imageUrl?.startsWith('/uploads/') ? `${API_BASE}${result.assets.imageUrl}` : result.assets?.imageUrl}
                  />
                  <a
                    href={videoUrl.startsWith('/uploads/') ? `${API_BASE}${videoUrl}` : videoUrl}
                    download
                    style={{ color: '#93c5fd' }}
                  >Download Video</a>
                  {adVideoId && (
                    <Link
                      to={`/ads/${encodeURIComponent(String(adVideoId))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#60a5fa' }}
                    >Share Ad →</Link>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      <Card title="Create Video" subtitle="Turn the image + audio into a short video">
        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 160px 180px', gap: 12 }}>
            <select value={videoMode} onChange={(e) => setVideoMode(e.target.value)} style={{ padding: 10, borderRadius: 8, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb', fontSize: 16 }}>
              <option value="static">Static (image + audio)</option>
              <option value="motion">Motion (image → video)</option>
              <option value="animated">Animated (prompt → video)</option>
            </select>
            <select value={aspect} onChange={(e) => setAspect(e.target.value)} style={{ padding: 10, borderRadius: 8, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb', fontSize: 16 }}>
              <option value="1:1">1:1 (Square)</option>
              <option value="9:16">9:16 (Vertical)</option>
              <option value="16:9">16:9 (Wide)</option>
            </select>
            <input type="number" min={5} max={120} step={5} value={duration} onChange={(e) => setDuration(Number(e.target.value) || 30)} style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb', fontSize: 16 }} />
            <select value={tier} onChange={(e) => setTier(e.target.value)} style={{ padding: 10, borderRadius: 8, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb', fontSize: 16 }}>
              <option value="low">Low (Budget)</option>
              <option value="medium">Medium (Standard)</option>
              <option value="high">High</option>
              <option value="veo3">Veo‑3 (Excellent)</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Button
              loading={videoBusy}
              disabled={videoBusy || !assetsReady || (typeof credits === 'number' && typeof estimCredits === 'number' && String(process.env?.ENFORCE_CREDITS) === 'true' && credits < estimCredits)}
              onClick={async () => {
                try {
                  setVideoBusy(true);
                  setVideoUrl('');
                  setProviderMeta(null);
                  // Async start
                  if (!ensureAudioReady()) {
                    setVideoBusy(false);
                    return;
                  }
                  const start = await api.adVideoStart({
                    streamId: result?.item?._id || result?.item?.id || undefined,
                    imageUrl: result?.assets?.imageUrl,
                    audioPath: result?.assets?.audioPath,
                    durationSec: duration,
                    aspectRatio: aspect,
                    prompt: prompt,
                    mode: videoMode,
                    tier: tier,
                    sfx: sfxSelected,
                    voFadeOutSec: Number(voFadeOutSec) || 0,
                    ...(showAdvanced ? {
                      tailPadSec: Number(tailPadSec) || 0,
                      voiceOffsetMs: Number(voiceOffsetMs) || 0,
                      visualPreset: visualPreset,
                      musicBed: musicBed,
                      musicTrack: musicTrack || undefined,
                    } : {}),
                  });
                  if (start?.error) {
                    if (start.error === 'payment_required') {
                      toast({ type: 'warning', message: `Insufficient credits. Need ~${start.estimateCredits}, have ${start.balance}.` });
                      return;
                    }
                    throw new Error(start.error);
                  }
                  if (!start?.predictionId) {
                    toast({ type: 'error', message: 'Video start failed (missing prediction)' });
                    return;
                  }
                  setPredId(start.predictionId);
                  setPredStatus('starting');
                  if (start?.meta) setProviderMeta(start.meta);
                  const provider = String(start?.meta?.provider || '');
                  if (provider === 'ffmpeg') {
                    toast({ type: 'success', message: 'Static video ready' });
                    setVideoUrl(resolveMediaUrl(start?.meta?.videoUrl || ''));
                    setVideoBusy(false);
                    return;
                  }
                  // Poll
                  let status = 'starting';
                  let tries = 0;
                  while (tries < 120) { // up to ~4 minutes
                    const st = await api.adVideoStatus(start.predictionId);
                    if (st?.error) throw new Error(st.error);
                    status = st?.status || 'unknown';
                    setPredStatus(status);
                    if (st?.meta) setProviderMeta(st.meta);
                    // Show provisional preview from provider output as soon as available
                    if (!videoUrl && st?.outputUrl) {
                      setVideoUrl(st.outputUrl);
                    }
                    if (status === 'succeeded' || status === 'failed' || status === 'canceled') break;
                    await new Promise(r => setTimeout(r, 2000));
                    tries++;
                  }
                  if (status !== 'succeeded') throw new Error(`video_status_${status || 'unknown'}`);
                  // Finalize
                  const fin = await api.adVideoFinalize({
                    predictionId: start.predictionId,
                    streamId: result?.item?._id || result?.item?.id || undefined,
                    audioPath: result?.assets?.audioPath,
                    imageUrl: result?.assets?.imageUrl,
                    durationSec: duration,
                    aspectRatio: aspect,
                    sfx: sfxSelected,
                    ...(showAdvanced ? {
                      tailPadSec: Number(tailPadSec) || 0,
                      voFadeOutSec: Number(voFadeOutSec) || 0,
                      voiceOffsetMs: Number(voiceOffsetMs) || 0,
                      visualPreset: visualPreset,
                      musicBed: musicBed,
                      musicTrack: musicTrack || undefined,
                    } : {}),
                  });
                  if (fin?.error) throw new Error(fin.error);
                  const url = fin?.item?.videoUrl;
                  const avid = fin?.meta?.adVideoId || fin?.item?.adVideoId;
                  if (fin?.meta) setProviderMeta(m => ({ ...(m || {}), ...fin.meta }));
                  if (url) {
                    setVideoUrl(url);
                    if (avid) setAdVideoId(String(avid));
                    toast({ type: 'success', message: 'Video ready' });
                  } else {
                    toast({ type: 'warning', message: 'Finalized but no URL returned' });
                  }
                } catch (e) {
                  toast({ type: 'error', message: e?.message || 'Create video failed' });
                } finally {
                  setVideoBusy(false);
                }
              }}
            >{videoBusy ? 'Creating…' : 'Create Video'}</Button>
            {!assetsReady && <span style={{ color: '#9ca3af', fontSize: 12 }}>Compose audio+image first</span>}
            {predId && <span style={{ color: '#9ca3af', fontSize: 12 }}>Prediction: {predId} • {predStatus}</span>}
          </div>
          <div style={{ color: '#9ca3af', fontSize: 12 }}>
            {typeof estimCredits === 'number' && <div>Estimated credits: {estimCredits}</div>}
            {typeof credits === 'number' && <div>Your credits: {credits}</div>}
          </div>
          <div>
            <div style={{ color: '#e5e7eb', marginBottom: 6 }}>Sound Effects</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ border: '1px solid #374151', borderRadius: 8, padding: 8 }}>
                <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 6 }}>Library</div>
                <div style={{ display: 'grid', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
                  {sfxCatalog.map((it, idx) => (
                    <div key={`${it.name}_${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button
                        onClick={() => addSfxQuick(it)}
                        style={{ background: '#111827', color: '#ff7a45', border: '1px solid #374151', borderRadius: 6, padding: '2px 6px', fontSize: 12, cursor: 'pointer' }}
                        title="Add with default timing"
                      >+ Add</button>
                      <span style={{ flex: 1, color: '#e5e7eb', fontSize: 12 }}>{it.name}</span>
                      <audio src={`${API_BASE}/uploads/assets/sfx/${it.file}`} controls preload="none" style={{ height: 24 }} />
                    </div>
                  ))}
                  {!sfxCatalog.length && <div style={{ color: '#9ca3af', fontSize: 12 }}>No SFX found</div>}
                </div>
              </div>
              <div style={{ border: '1px solid #374151', borderRadius: 8, padding: 8 }}>
                <div style={{ color: '#9ca3af', fontSize: 12, marginBottom: 6 }}>Selected</div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {sfxSelected.map((s, i) => (
                    <div key={`sel_${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: '#e5e7eb', fontSize: 12, minWidth: 120 }}>{s.name}</span>
                      <input
                        type="number"
                        value={s.atMs}
                        onChange={e => {
                          const v = Math.max(0, Number(e.target.value) || 0);
                          setSfxSelected(prev => prev.map((x, idx) => idx === i ? { ...x, atMs: v } : x));
                        }}
                        style={{ width: 100, padding: 6, borderRadius: 6, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb', fontSize: 12 }}
                      />
                      <span style={{ color: '#9ca3af', fontSize: 12 }}>ms</span>
                      <button onClick={() => removeSfxAt(i)} style={{ marginLeft: 'auto', background: '#1f2937', color: '#fca5a5', border: '1px solid #374151', borderRadius: 6, padding: '2px 6px', fontSize: 12, cursor: 'pointer' }}>Remove</button>
                    </div>
                  ))}
                  {!sfxSelected.length && <div style={{ color: '#9ca3af', fontSize: 12 }}>No SFX selected</div>}
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button onClick={async ()=>{ const r=await api.checkoutCredits('100'); if (r?.url) window.location.href=r.url; }} plain>Buy 100 credits</Button>
            <Button onClick={async ()=>{ const r=await api.checkoutCredits('500'); if (r?.url) window.location.href=r.url; }} plain>Buy 500 credits</Button>
            <Button onClick={async ()=>{ const r=await api.checkoutCredits('2000'); if (r?.url) window.location.href=r.url; }} plain>Buy 2000 credits</Button>
          </div>
          {providerMeta && (
            <div style={{ color: '#9ca3af', fontSize: 12 }}>
              <div>Provider: {providerMeta.provider || 'n/a'}</div>
              {providerMeta.videoModel && <div>Model: {String(providerMeta.videoModel)}</div>}
              {providerMeta.videoVersion && <div>Version: {String(providerMeta.videoVersion)}</div>}
              {providerMeta.mode && <div>Mode: {String(providerMeta.mode)}</div>}
              {providerMeta.aspectRatio && <div>Aspect: {String(providerMeta.aspectRatio)}</div>}
              {providerMeta.source && <div>Source: {String(providerMeta.source)}</div>}
              {typeof providerMeta.estimateCredits === 'number' && <div>Est. credits (server): {providerMeta.estimateCredits}</div>}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
